import { isTrackedClass, ITrackedClass } from '../../interfaces/tracked-class';
import { isPrimitive } from '../primitives/primitive-utilities';
import { Token } from '../token';
import { TrackChanges, TrackChangesOptions } from './change-tracker';
import { instanceOf } from './model-utils';

/** Convenience decorator that sets up default options for Kb3dObjects with the change tracker */
export function TrackChangesNode<T = any>(options: TrackChangesOptions<T> = {}) {
    const origAddedToChildArray = options.addedToChildArray;
    options.addedToChildArray = (parent: T, item: any, arrayPropName: string) => {
        if (origAddedToChildArray) origAddedToChildArray(parent, item, arrayPropName);
        if (instanceOf(parent, Token.Kb3dObject) && instanceOf(item, Token.Kb3dObject)) {
            item._parent = parent;
            if (isTrackedClass(parent) && (parent as any)._trackChanges) {
                item.calculateParents();
            }
        }
    };

    const origDestroy = options.onDestroy;
    options.onDestroy = (item: T & ITrackedClass) => {
        if (origDestroy) origDestroy(item);

        const children = item.getChildren();
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (isTrackedClass(child)) {
                child.destroy();
            }
        }

        if (item._parent && instanceOf(item._parent, Token.Kb3dObject) && instanceOf(item, Token.Kb3dObject)) {
            if (item._parent._manager) {
                item._parent._manager.unregister(item);
            }
        }

        item._parent = undefined;
        item.isModified = false;
    };

    if (!options.isPropertyEqual) {
        options.isPropertyEqual = (oldValue, newValue, propertyKey) => {
            let result: boolean | undefined;

            // Check equality on node properties by handling arrays and KB primitives. Fall back to strict equality checks for other values.
            if (Array.isArray(oldValue) && Array.isArray(newValue)) {
                if (oldValue.length === newValue.length) {
                    let isEqual = true;

                    for (let i = 0; i < newValue.length; i++) {
                        if (newValue[i] !== oldValue[i]) {
                            isEqual = false;
                            break;
                        }
                    }
                    result = isEqual;
                } else {
                    result = false;
                }
            } else if (isPrimitive(newValue)) {
                result = typeof oldValue !== 'undefined' && newValue.equal(oldValue);
            }
            // console.log('checking equality', oldValue, newValue, result);
            return result;
        };
    }

    return TrackChanges(options);
}
